import axios from 'axios';
import type {
  Bank,
  Statement,
  UploadStatementResponse,
  GetBanksResponse,
  GetStatementsResponse,
  Category,
  CategorySuggestion,
} from '../types';

// API URL configuration - Updated 2025-11-19
// In production, ALWAYS use relative /api paths - Vercel proxy will route to backend
// In development, use localhost backend directly
// IMPORTANT: Check MODE first, before checking VITE_API_URL, to ensure production uses /api
const API_URL = import.meta.env.MODE === 'production'
  ? '/api'  // Production ALWAYS uses relative path - Vercel proxy handles routing
  : (import.meta.env.VITE_API_URL || 'http://localhost:3001/api');

// Create axios instance
const apiClient = axios.create({
  baseURL: API_URL,
  // Don't set default Content-Type - let axios set it automatically
  // For JSON requests: application/json
  // For FormData requests: multipart/form-data with boundary
});

// Generate or get guest user ID
function getGuestUserId(): string {
  let guestId = localStorage.getItem('guestUserId');
  if (!guestId) {
    guestId = `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('guestUserId', guestId);
  }
  return guestId;
}

// Add auth token and guest user ID to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    // For guest users, send the guest user ID in a custom header
    const guestId = getGuestUserId();
    config.headers['X-Guest-User-Id'] = guestId;
  }
  return config;
});

// API methods
export const api = {
  // Banks
  getBanks: async (): Promise<Bank[]> => {
    const response = await apiClient.get<GetBanksResponse>('/banks');
    return response.data.banks;
  },

  // Statements
  detectBankFromStatement: async (file: File): Promise<{
    detected: boolean;
    bank?: { id: string; name: string; code: string; confidence: number };
    message: string;
    timestamp: string;
  }> => {
    const formData = new FormData();
    formData.append('statement', file);

    // Let axios automatically set Content-Type with boundary
    const response = await apiClient.post('/statements/detect-bank', formData);
    return response.data;
  },

  uploadStatement: async (file: File, bankId: string): Promise<UploadStatementResponse> => {
    const formData = new FormData();
    formData.append('statement', file);
    formData.append('bankId', bankId);
    formData.append('guestUserId', getGuestUserId());

    // Let axios automatically set Content-Type with boundary
    const response = await apiClient.post<UploadStatementResponse>(
      '/statements/upload',
      formData
    );
    return response.data;
  },

  getStatements: async (): Promise<Statement[]> => {
    const response = await apiClient.get<GetStatementsResponse>('/statements');
    return response.data.statements;
  },

  getStatementById: async (id: string): Promise<Statement> => {
    const response = await apiClient.get<{ statement: Statement }>(`/statements/${id}`);
    return response.data.statement;
  },

  deleteStatement: async (id: string): Promise<void> => {
    await apiClient.delete(`/statements/${id}`);
  },

  // Auth (simplified for testing)
  login: async (email: string, password: string) => {
    const response = await apiClient.post('/auth/login', { email, password });
    const { accessToken } = response.data.tokens;
    localStorage.setItem('accessToken', accessToken);
    return response.data;
  },

  register: async (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    guestUserId?: string;
  }) => {
    const response = await apiClient.post('/auth/register', data);
    const { accessToken } = response.data.tokens;
    localStorage.setItem('accessToken', accessToken);
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('accessToken');
  },

  // Categories
  getCategories: async (): Promise<{ categories: Category[] }> => {
    const response = await apiClient.get<{ categories: Category[] }>('/categories');
    return response.data;
  },

  getCategoriesGrouped: async (): Promise<{ income: Category[]; expense: Category[] }> => {
    const response = await apiClient.get<{ income: Category[]; expense: Category[] }>('/categories/grouped');
    return response.data;
  },

  // Transactions
  getAllTransactions: async (): Promise<{ transactions: any[] }> => {
    const response = await apiClient.get('/transactions');
    return response.data;
  },

  getCategorySuggestions: async (transactionId: string): Promise<CategorySuggestion[]> => {
    const response = await apiClient.get<{ suggestions: CategorySuggestion[] }>(
      `/transactions/${transactionId}/suggestions`
    );
    return response.data.suggestions;
  },

  updateTransactionCategory: async (transactionId: string, categoryId: string) => {
    const response = await apiClient.patch(`/transactions/${transactionId}/category`, {
      categoryId,
    });
    return response.data;
  },

  autoCategorizeTransactions: async () => {
    const response = await apiClient.post('/transactions/auto-categorize');
    return response.data;
  },

  deleteTransaction: async (transactionId: string) => {
    const response = await apiClient.delete(`/transactions/${transactionId}`);
    return response.data;
  },

  // Duplicate Detection & Data Quality
  getDuplicates: async (): Promise<{
    duplicateGroups: Array<{
      key: string;
      transactions: any[];
      confidence: 'high' | 'medium' | 'low';
    }>;
    totalDuplicates: number;
    potentialSavings: number;
  }> => {
    const response = await apiClient.get('/transactions/duplicates');
    return response.data;
  },

  mergeDuplicates: async (primaryTransactionId: string, duplicateIds: string[]) => {
    const response = await apiClient.post('/transactions/duplicates/merge', {
      primaryTransactionId,
      duplicateIds,
    });
    return response.data;
  },

  updateTransaction: async (
    transactionId: string,
    updates: {
      description?: string;
      merchantName?: string;
      amount?: number;
      transactionDate?: string;
      categoryId?: string | null;
    }
  ) => {
    const response = await apiClient.patch(`/transactions/${transactionId}`, updates);
    return response.data;
  },

  createManualTransaction: async (data: {
    description: string;
    amount: number;
    transactionDate: string;
    merchantName?: string;
    categoryId?: string;
    currency?: string;
  }) => {
    const response = await apiClient.post('/transactions/manual', data);
    return response.data;
  },

  // Bulk Operations
  bulkDeleteTransactions: async (transactionIds: string[]) => {
    const response = await apiClient.post('/transactions/bulk/delete', {
      transactionIds,
    });
    return response.data;
  },

  bulkCategorizeTransactions: async (transactionIds: string[], categoryId: string) => {
    const response = await apiClient.post('/transactions/bulk/categorize', {
      transactionIds,
      categoryId,
    });
    return response.data;
  },

  exportTransactions: async (transactionIds?: string[], format: 'csv' = 'csv'): Promise<Blob> => {
    const response = await apiClient.post(
      '/transactions/export',
      {
        transactionIds,
        format,
      },
      {
        responseType: 'blob',
      }
    );
    return response.data;
  },

  // Recurring Transactions
  getRecurringTransactions: async (minOccurrences: number = 3): Promise<{
    recurringPatterns: Array<{
      id: string;
      merchantName: string;
      description: string;
      averageAmount: number;
      frequency: 'weekly' | 'monthly' | 'quarterly';
      dayOfPeriod: number;
      confidence: 'high' | 'medium' | 'low';
      transactions: any[];
      nextExpectedDate: string;
      lastAmount: number;
      isActive: boolean;
      variance: number;
    }>;
    totalRecurring: number;
    predictedMonthlyExpense: number;
    missingTransactions: Array<{
      pattern: any;
      expectedDate: string;
      daysMissing: number;
    }>;
  }> => {
    const response = await apiClient.get(`/transactions/recurring?minOccurrences=${minOccurrences}`);
    return response.data;
  },

  updateRecurringPatternStatus: async (patternId: string, status: 'tracked' | 'ignored') => {
    const response = await apiClient.post(`/transactions/recurring/${patternId}/status`, { status });
    return response.data;
  },

  // Analytics
  getAnalytics: async (): Promise<{
    analytics: {
      monthOverMonth: any | null;
      yearOverYear: any | null;
      categoryAverages: Array<{
        categoryId: string;
        categoryName: string;
        categoryIcon: string;
        categoryColor: string;
        type: 'income' | 'expense';
        totalAmount: number;
        averagePerTransaction: number;
        transactionCount: number;
        averagePerMonth: number;
      }>;
      topMerchants: Array<{
        merchantName: string;
        totalSpent: number;
        transactionCount: number;
        averageTransaction: number;
        category?: {
          name: string;
          icon: string;
          color: string;
        };
      }>;
      spendingHeatmap: Array<{
        dayOfWeek: number;
        hour: number;
        amount: number;
        transactionCount: number;
      }>;
      cashflowForecast: Array<{
        month: string;
        predictedIncome: number;
        predictedExpenses: number;
        predictedNet: number;
        confidence: 'high' | 'medium' | 'low';
      }>;
    };
  }> => {
    const response = await apiClient.get('/analytics');
    return response.data;
  },

  // Stats
  getStats: async (): Promise<{
    totalBanks: number;
    operationalBanks: number;
    totalTransactions: number;
    roundedTransactions: number;
    timestamp: string;
  }> => {
    const response = await apiClient.get('/stats');
    return response.data;
  },

  // Bank Requests
  submitBankRequest: async (data: {
    bankName: string;
    contactEmail?: string;
    sampleStatement?: File;
  }): Promise<{
    message: string;
    request: {
      id: string;
      bankName: string;
      status: string;
      createdAt: string;
    };
    timestamp: string;
  }> => {
    const formData = new FormData();
    formData.append('bankName', data.bankName);
    if (data.contactEmail) {
      formData.append('contactEmail', data.contactEmail);
    }
    if (data.sampleStatement) {
      formData.append('sampleStatement', data.sampleStatement);
    }

    // Let axios automatically set Content-Type with boundary
    const response = await apiClient.post('/bank-requests', formData);
    return response.data;
  },
};

export default apiClient;
